import { html, CSSResultGroup } from 'lit';
import { property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { BlocksElement } from '../../BlocksElement';
import styles from '../icon.styles';
import type { IconColorType, IconSize } from './icon.model';

/**
 * Menu component
 * @csspart base- The component's base wrapper
 */
export class BlocksIconMenu extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Icon color variant
   * <cat-text-passage size="sm">
   * <ul>
   * <li>No color defined renders an icon with the currentColor of its parent element</li>
   * <li>**content-strong** renders an icon with the cat-theme-color-content-strong color token</li>
   * <li>**knockout** renders an icon with the cat-theme-color-content-knockout color token</li>
   * <li>**knockout-brand** renders a renders an icon with the cat-theme-color-content-knockout-brand color token</li>
   * <li>**information** renders an icon with the cat-theme-color-content-utility-information color token</li>
   * <li>**success** renders an icon with the cat-theme-color-content-utility-success color token</li>
   * <li>**warning** renders a renders an icon with the cat-theme-color-content-utility-warning color token</li>
   * <li>**error** renders an icon with the cat-theme-color-content-utility-error color token</li>
   * </ul>
   * </cat-text-passage>
   */
  @property({ type: String })
  color: IconColorType;

  /**
   * Title
   * 1) Icons should always have associated text. Use this to supply `aria-label` to icon if it stands by itself without any visual/visually-hidden text
   */
  @property({ type: String })
  iconTitle: string | undefined;

  /**
   * Size property
   * - Default size is 16px
   * - **sm** renders a larger size than default (12px)
   * - **lg** renders a larger size than default (24px)
   * - **xl** renders a larger size than the lg variant (32px);
   */
  @property({ type: String })
  size: IconSize;

  connectedCallback() {
    super.connectedCallback();
    this.classList.add('cat-icon');
  }

  render() {
    const componentClassName = this.componentClassNames('cat-c-icon', {
      'cat-c-icon--sm': this.size === 'sm',
      'cat-c-icon--lg': this.size === 'lg',
      'cat-c-icon--xl': this.size === 'xl',
      'cat-c-icon--content-strong': this.color === 'content-strong',
      'cat-c-icon--knockout': this.color === 'knockout',
      'cat-c-icon--knockout-brand': this.color === 'knockout-brand',
      'cat-c-icon--information': this.color === 'information',
      'cat-c-icon--success': this.color === 'success',
      'cat-c-icon--warning': this.color === 'warning',
      'cat-c-icon--error': this.color === 'error'
    });

    return html`
      <span part="base" class="${componentClassName}" role="presentation">
        <svg
          aria-hidden="${!this.iconTitle}"
          aria-label="${ifDefined(this.iconTitle)}"
          role="${this.iconTitle && 'img'}"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.10449 4.19589C1.10449 3.78168 1.44028 3.44589 1.85449 3.44589H22.1445C22.5587 3.44589 22.8945 3.78168 22.8945 4.19589C22.8945 4.61011 22.5587 4.94589 22.1445 4.94589H1.85449C1.44028 4.94589 1.10449 4.61011 1.10449 4.19589Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.10449 11.9998C1.10449 11.5855 1.44028 11.2498 1.85449 11.2498H22.1445C22.5587 11.2498 22.8945 11.5855 22.8945 11.9998C22.8945 12.414 22.5587 12.7498 22.1445 12.7498H1.85449C1.44028 12.7498 1.10449 12.414 1.10449 11.9998Z"
          />
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M1.10449 19.8036C1.10449 19.3894 1.44028 19.0536 1.85449 19.0536H22.1445C22.5587 19.0536 22.8945 19.3894 22.8945 19.8036C22.8945 20.2178 22.5587 20.5536 22.1445 20.5536H1.85449C1.44028 20.5536 1.10449 20.2178 1.10449 19.8036Z"
          />
        </svg>
      </span>
    `;
  }
}

if (customElements.get('cat-icon-menu') === undefined) {
  customElements.define('cat-icon-menu', BlocksIconMenu);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-icon-menu': BlocksIconMenu;
  }
}
